import { useQuery, useMutation, useQueryClient, useInfiniteQuery, useQueries } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import api, { authApi, walletApi, apiKeysApi, usageApi, adminApi, profileApi, subscriptionApi, supportApi, modelsApi, messagesApi, configApi, partnerRepApi, subApi } from "../services/api";

function extractErrorDetail(err: unknown): string | undefined {
  const data = (err as Record<string, unknown>)?.response as Record<string, unknown> | undefined;
  const payload = (data?.data ?? (err as Record<string, unknown>)?.data) as Record<string, unknown> | undefined;
  if (!payload) return undefined;
  const raw = payload.detail;
  if (typeof raw === "string" && raw.trim()) {
    return raw.replace(/^Value error,\s*/i, "").trim();
  }
  if (Array.isArray(raw) && raw.length > 0) {
    const first = raw[0] as Record<string, unknown>;
    const msg = (first.msg as string) || (first.message as string) || "";
    if (msg) return msg.replace(/^Value error,\s*/i, "").trim();
  }
  if (raw && typeof raw === "object") {
    const obj = raw as Record<string, unknown>;
    if (typeof obj.message === "string" && obj.message.trim()) return obj.message.trim();
    if (typeof obj.msg === "string" && obj.msg.trim()) return (obj.msg as string).replace(/^Value error,\s*/i, "").trim();
  }
  if (typeof payload.message === "string" && (payload.message as string).trim()) return (payload.message as string).trim();
  if (typeof payload.msg === "string" && (payload.msg as string).trim()) return (payload.msg as string).trim();
  return undefined;
}

function translateDetail(detail: string): string {
  const map: Record<string, string> = {
    "This username is not allowed": "این نام کاربری مجاز نیست",
    "Username must be more than 4 characters": "نام کاربری باید بیشتر از ۴ کاراکتر باشد",
    "Username already taken": "این نام کاربری قبلا ثبت شده است",
    "Email already registered": "این ایمیل قبلا ثبت شده است",
  };
  return map[detail] || detail;
}

// ============================================================================
// Auth
// ============================================================================

export function useMe() {
  return useQuery({
    queryKey: ["me"],
    queryFn: () => authApi.me().then((r) => r.data),
    retry: false,
    staleTime: 5 * 60 * 1000,
  });
}

export function useLogin() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: (data: { email: string; password: string }) =>
      authApi.login(data).then((r) => r.data),
    onSuccess: (data) => {
      queryClient.setQueryData(["me"], data.user);
      toast.success("ورود موفقیت‌آمیز");
      navigate("/dashboard");
    },
    onError: (err: unknown) => {
      toast.error(extractErrorDetail(err) ? translateDetail(extractErrorDetail(err)!) : "خطا در ورود");
    },
  });
}

export function useRegister() {
  const navigate = useNavigate();

  return useMutation({
    mutationFn: (data: { email: string; password: string; name?: string; username?: string }) =>
      authApi.register(data).then((r) => r.data),
    onSuccess: () => {
      toast.success("ثبت‌نام موفقیت‌آمیز");
      navigate("/login");
    },
    onError: (err: unknown) => {
      const raw = extractErrorDetail(err);
      const msg = raw ? translateDetail(raw) : "خطا در ثبت‌نام";
      toast.error(msg);
    },
  });
}

export function useLogout() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: () => authApi.logout().then((r) => r.data),
    onSuccess: () => {
      queryClient.clear();
      navigate("/login");
    },
  });
}

export function useOtpSend() {
  return useMutation({
    mutationFn: (data: { phone: string }) => authApi.otpSend(data).then((r) => r.data),
    onError: (err: unknown) => {
      toast.error(extractErrorDetail(err) ? translateDetail(extractErrorDetail(err)!) : "خطا در ارسال کد");
    },
  });
}

export function useOtpVerify() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  return useMutation({
    mutationFn: (data: { phone: string; code: string }) =>
      authApi.otpVerify(data).then((r) => r.data),
    onSuccess: (data) => {
      queryClient.setQueryData(["me"], data.user);
      toast.success("ورود موفقیت‌آمیز");
      navigate("/dashboard");
    },
    onError: (err: unknown) => {
      toast.error(extractErrorDetail(err) ? translateDetail(extractErrorDetail(err)!) : "کد نامعتبر یا منقضی شده");
    },
  });
}

// ============================================================================
// Wallet
// ============================================================================

export function useWalletBalance() {
  return useQuery({
    queryKey: ["wallet", "balance"],
    queryFn: () => walletApi.getBalance().then((r) => r.data),
  });
}

export function useWalletTransactions(limit = 50, offset = 0) {
  return useQuery({
    queryKey: ["wallet", "transactions", limit, offset],
    queryFn: () => walletApi.getTransactions({ limit, offset }).then((r) => r.data),
  });
}

export function useWalletDeposit() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (amount: number) => walletApi.deposit(amount).then((r) => r.data),
    onSuccess: (data) => {
      // Redirect to ZarinPal payment page
      if (data.payment_url) {
        window.location.href = data.payment_url;
      }
    },
    onError: (err: Record<string, unknown>) => {
      const data = (err.response as Record<string, unknown>)?.data as Record<string, unknown>;
      const detail = data?.detail as unknown;
      // Backend phone_not_verified guard returns {code, message}
      if (detail && typeof detail === "object" && (detail as Record<string, unknown>).code === "phone_not_verified") {
        const msg = (detail as Record<string, unknown>).message as string || "برای شارژ کیف پول ابتدا شماره موبایل خود را تایید کنید.";
        toast.error(msg);
        return;
      }
      const paymentId = data?.payment_id || 0;
      const message = data?.message || (data?.detail as string) || "خطا در درخواست پرداخت";
      // If detail was object, message already handled above; fallback
      const msgStr = typeof message === "string" ? message : "خطا در درخواست پرداخت";
      window.location.href = `/payment-fail?payment_id=${paymentId}&reason=${encodeURIComponent(msgStr)}`;
    },
  });
}

export function useWalletPayments(limit = 50, offset = 0) {
  return useQuery({
    queryKey: ["wallet", "payments", limit, offset],
    queryFn: () => walletApi.getPayments({ limit, offset }).then((r) => r.data),
  });
}

export function useWalletDepositInfo() {
  return useQuery({
    queryKey: ["wallet", "deposit-info"],
    queryFn: () => walletApi.getDepositInfo().then((r) => r.data),
    staleTime: 5 * 60 * 1000,
  });
}

// ============================================================================
// API Keys
// ============================================================================

export function useApiKeys() {
  return useQuery({
    queryKey: ["api-keys"],
    queryFn: () => apiKeysApi.list().then((r) => r.data),
  });
}

export function useApiKeyUsageSummary(apiKeyId: string | number | null) {
  return useQuery({
    queryKey: ["api-keys", "usage-summary", apiKeyId],
    queryFn: () => usageApi.getSummary({ api_key_id: apiKeyId }).then((r) => r.data),
    enabled: apiKeyId !== null,
    staleTime: 30 * 1000,
  });
}

export function useApiKeyUsageDashboard(apiKeyId: string | number | null, duration: string, metric: string) {
  return useQuery({
    queryKey: ["api-keys", "usage-dashboard", apiKeyId, duration, metric],
    queryFn: () =>
      usageApi
        .getDashboard({ api_key_id: apiKeyId, duration, metric })
        .then((r) => r.data),
    enabled: apiKeyId !== null,
    staleTime: 30 * 1000,
  });
}

export function useUpdateApiKey() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string | number; data: { name?: string; status?: string; credit_limit?: number | null } }) =>
      apiKeysApi.update(id, data).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["api-keys"] });
      toast.success("کلید API به‌روزرسانی شد");
    },
    onError: (err: unknown) => {
      toast.error(extractErrorDetail(err) ? translateDetail(extractErrorDetail(err)!) : "خطا در به‌روزرسانی کلید API");
    },
  });
}

export function useCreateApiKey() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: { name: string; credit_limit?: number | null }) =>
      apiKeysApi.create(data).then((r) => r.data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["api-keys"] });
      toast.success("کلید API ساخته شد");
      // Return the full key for display
      return data;
    },
    onError: () => toast.error("خطا در ساخت کلید API"),
  });
}

export function useRevokeApiKey() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => apiKeysApi.revoke(id).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["api-keys"] });
      toast.success("کلید API لغو شد");
    },
    onError: () => toast.error("خطا در لغو کلید API"),
  });
}

// ============================================================================
// Usage
// ============================================================================

export function useUsageLogs(params?: Record<string, unknown>) {
  return useQuery({
    queryKey: ["usage", "logs", params],
    queryFn: () => usageApi.getLogs(params).then((r) => r.data),
    staleTime: 30 * 1000,
  });
}

export function useUsageSummary(params?: Record<string, unknown>) {
  return useQuery({
    queryKey: ["usage", "summary", params],
    queryFn: () => usageApi.getSummary(params).then((r) => r.data),
    staleTime: 30 * 1000,
  });
}

export function useUsageGroup(by: string, params?: Record<string, unknown>) {
  return useQuery({
    queryKey: ["usage", "group", by, params],
    queryFn: () => usageApi.getGroup({ by, ...params }).then((r) => r.data),
    staleTime: 30 * 1000,
  });
}

export function useUsageFilters() {
  return useQuery({
    queryKey: ["usage", "filters"],
    queryFn: () => usageApi.getFilters().then((r) => r.data),
    staleTime: 60 * 1000,
  });
}

export function useUsageTimeseries(duration: string, filters?: Record<string, unknown>) {
  return useQuery({
    queryKey: ["usage", "timeseries", duration, filters],
    queryFn: () => usageApi.getTimeseries({ ...filters, duration }).then((r) => r.data),
    staleTime: 30 * 1000,
  });
}

export function useUsageStacked(duration: string) {
  return useQuery({
    queryKey: ["usage", "stacked", duration],
    queryFn: () => usageApi.getStacked({ duration }).then((r) => r.data),
    staleTime: 30 * 1000,
  });
}

export function useUsageDashboard(duration: string, metric: string) {
  return useQuery({
    queryKey: ["usage", "dashboard", duration, metric],
    queryFn: () => usageApi.getDashboard({ duration, metric }).then((r) => r.data),
    staleTime: 30 * 1000,
  });
}

export const DASHBOARD_METRIC_VALUES = ["tokens", "spend", "requests"] as const;

export function useUsageDashboards(duration: string) {
  return useQueries({
    queries: DASHBOARD_METRIC_VALUES.map((metric) => ({
      queryKey: ["usage", "dashboard", duration, metric],
      queryFn: () => usageApi.getDashboard({ duration, metric }).then((r) => r.data),
      staleTime: 30 * 1000,
    })),
    combine: (results) => {
      const byMetric = {} as Record<string, unknown>;
      DASHBOARD_METRIC_VALUES.forEach((metric, i) => {
        byMetric[metric] = results[i]?.data;
      });
      return {
        byMetric,
        results,
        isLoading: results.some((r) => r.isLoading && !r.data),
        isFetching: results.some((r) => r.isFetching),
      };
    },
  });
}

export function useUsageActivity() {
  return useQuery({
    queryKey: ["usage", "activity"],
    queryFn: () => usageApi.getActivity().then((r) => r.data),
    staleTime: 30 * 1000,
  });
}

export function useUsageSankeyKeys(duration: string) {
  return useQuery({
    queryKey: ["usage", "sankey", "keys", duration],
    queryFn: () => usageApi.getSankeyKeys({ duration }).then((r) => r.data),
    staleTime: 30 * 1000,
  });
}

export function useUsageSankeyModels(duration: string) {
  return useQuery({
    queryKey: ["usage", "sankey", "models", duration],
    queryFn: () => usageApi.getSankeyModels({ duration }).then((r) => r.data),
    staleTime: 30 * 1000,
  });
}

export function useUsageLogsInfinite(params?: Record<string, unknown>, pageSize = 20) {
  return useInfiniteQuery({
    queryKey: ["usage", "logs", "infinite", params, pageSize],
    queryFn: ({ pageParam }) =>
      usageApi.getLogs({ ...params, limit: pageSize, offset: pageParam }).then((r) => r.data),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage || lastPage.length < pageSize) return undefined;
      return allPages.reduce((sum, page) => sum + (page?.length || 0), 0);
    },
    staleTime: 30 * 1000,
  });
}

// ============================================================================
// Admin
// ============================================================================

export function useAdminLogin() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: (data: { email: string; password: string }) =>
      adminApi.login(data).then((r) => r.data),
    onSuccess: (data) => {
      queryClient.setQueryData(["me"], data.user);
      queryClient.invalidateQueries({ queryKey: ["admin"] });
      navigate(data?.redirect || "/admin/dashboard");
    },
    onError: (err: unknown) => {
      toast.error(extractErrorDetail(err) ? translateDetail(extractErrorDetail(err)!) : "خطا در ورود مدیر");
    },
  });
}

export function useAdminRecoverySend() {
  return useMutation({
    mutationFn: (data: { identifier: string }) =>
      adminApi.recoverySend(data).then((r) => r.data),
    onError: (err: unknown) => {
      toast.error(extractErrorDetail(err) ? translateDetail(extractErrorDetail(err)!) : "خطا در ارسال کد");
    },
  });
}

export function useAdminRecoveryVerify() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  return useMutation({
    mutationFn: (data: { identifier: string; code: string }) =>
      adminApi.recoveryVerify(data).then((r) => r.data),
    onSuccess: (data) => {
      queryClient.setQueryData(["me"], data.user);
      queryClient.invalidateQueries({ queryKey: ["admin"] });
      toast.success("ورود موفقیت‌آمیز");
      navigate(data?.redirect || "/admin/dashboard");
    },
    onError: (err: unknown) => {
      toast.error(extractErrorDetail(err) ? translateDetail(extractErrorDetail(err)!) : "کد نامعتبر یا منقضی شده");
    },
  });
}

export function useAdminLogout() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: () => adminApi.logout().then((r) => r.data),
    onSuccess: () => {
      queryClient.clear();
      navigate("/admin/login");
    },
  });
}

export function useAdminUsers(params?: Record<string, unknown>) {
  return useQuery({
    queryKey: ["admin", "users", params],
    queryFn: () => adminApi.getUsers(params).then((r) => r.data),
  });
}

export function useAdminUserUsage(userId: number | null) {
  return useQuery({
    queryKey: ["admin", "users", userId, "usage"],
    queryFn: () => adminApi.getUserUsage(userId as number).then((r) => r.data),
    enabled: userId !== null,
  });
}

export function useAdminUpdateUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) =>
      adminApi.updateUser(id, data).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "users"] });
      toast.success("کاربر به‌روزرسانی شد");
    },
    onError: () => toast.error("خطا در به‌روزرسانی کاربر"),
  });
}

export function useAdminDeleteUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => adminApi.deleteUser(id).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "users"] });
      toast.success("کاربر حذف شد");
    },
    onError: () => toast.error("خطا در حذف کاربر"),
  });
}

export function useAdminUpdateUserSubscription() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) =>
      adminApi.updateUserSubscription(id, data).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "users"] });
      toast.success("اشتراک کاربر به‌روزرسانی شد");
    },
    onError: (err: unknown) => {
      toast.error(extractErrorDetail(err) ? translateDetail(extractErrorDetail(err)!) : "خطا در به‌روزرسانی اشتراک");
    },
  });
}

export function useAdminModels(params?: Record<string, unknown>) {
  return useQuery({
    queryKey: ["admin", "models", params],
    queryFn: () => adminApi.getModels(params).then((r) => r.data),
  });
}

export function useAdminModelDetail(modelId: number | null) {
  return useQuery({
    queryKey: ["admin", "models", "detail", modelId],
    queryFn: () => adminApi.getModelDetail(modelId as number).then((r) => r.data),
    enabled: Boolean(modelId),
    staleTime: 60 * 1000,
  });
}

export function useAdminUpdateModel() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) =>
      adminApi.updateModel(id, data).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "models"] });
      toast.success("مدل به‌روزرسانی شد");
    },
    onError: () => toast.error("خطا در به‌روزرسانی مدل"),
  });
}

export function useAdminStats() {
  return useQuery({
    queryKey: ["admin", "stats"],
    queryFn: () => adminApi.getStats().then((r) => r.data),
  });
}

export function useAdminNotifications() {
  return useQuery({
    queryKey: ["admin", "notifications"],
    queryFn: () => adminApi.getNotifications().then((r) => r.data),
    refetchInterval: 30000,
  });
}

export function useAdminLogs(params?: Record<string, unknown>) {
  return useQuery({
    queryKey: ["admin", "logs", params],
    queryFn: () => adminApi.getLogs(params).then((r) => r.data),
    refetchInterval: 30000,
  });
}

export function useAdminLogFilters() {
  return useQuery({
    queryKey: ["admin", "logs", "filters"],
    queryFn: () => adminApi.getLogFilters().then((r) => r.data),
  });
}

export function useAdminRecoverLog() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, openrouter_generation_id }: { id: number; openrouter_generation_id?: string }) =>
      adminApi
        .recoverLog(id, openrouter_generation_id ? { openrouter_generation_id } : {})
        .then((r) => r.data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["admin", "logs"] });
      queryClient.invalidateQueries({ queryKey: ["admin", "notifications"] });
      if (data?.success) {
        toast.success("لاگ بازیابی و تسویه شد");
      } else if (data?.message) {
        toast.error(translateDetail(data.message));
      }
    },
    onError: (err: unknown) => {
      toast.error(
        extractErrorDetail(err) ? translateDetail(extractErrorDetail(err)!) : "خطا در بازیابی لاگ"
      );
    },
  });
}

export function useAdminRecoverStuckLogs() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (limit: number) => adminApi.recoverStuckLogs(limit).then((r) => r.data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["admin", "logs"] });
      queryClient.invalidateQueries({ queryKey: ["admin", "notifications"] });
      toast.success(`${data?.recovered ?? 0} از ${data?.total ?? 0} لاگ بازیابی شد`);
    },
    onError: (err: unknown) => {
      toast.error(
        extractErrorDetail(err)
          ? translateDetail(extractErrorDetail(err)!)
          : "خطا در بازیابی دسته‌ای لاگ‌ها"
      );
    },
  });
}

export function useAdminDeleteLog() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => adminApi.deleteLog(id).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "logs"] });
      queryClient.invalidateQueries({ queryKey: ["admin", "notifications"] });
      toast.success("لاگ حذف شد");
    },
    onError: (err: unknown) => {
      toast.error(
        extractErrorDetail(err)
          ? translateDetail(extractErrorDetail(err)!)
          : "خطا در حذف لاگ"
      );
    },
  });
}

export function useAdminFailLog() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => adminApi.failLog(id).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "logs"] });
      queryClient.invalidateQueries({ queryKey: ["admin", "notifications"] });
      toast.success("لاگ به‌عنوان ناموفق علامت شد");
    },
    onError: (err: unknown) => {
      toast.error(
        extractErrorDetail(err)
          ? translateDetail(extractErrorDetail(err)!)
          : "خطا در تغییر وضعیت لاگ"
      );
    },
  });
}

export function useAdminLogsInfinite(filters?: Record<string, unknown>, pageSize = 20) {
  return useInfiniteQuery({
    queryKey: ["admin", "logs", "infinite", filters, pageSize],
    queryFn: ({ pageParam }) =>
      adminApi.getLogs({ ...filters, limit: pageSize, offset: pageParam }).then((r) => r.data),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage || lastPage.length < pageSize) return undefined;
      return allPages.reduce((sum, page) => sum + (page?.length || 0), 0);
    },
    refetchInterval: 30000,
  });
}

export function useAdminSystem() {
  return useQuery({
    queryKey: ["admin", "system"],
    queryFn: () => adminApi.getSystem().then((r) => r.data),
    refetchInterval: 15000,
  });
}

export function useAdminSystemResources() {
  return useQuery({
    queryKey: ["admin", "system", "resources", "total"],
    queryFn: () => adminApi.getTotalResources().then((r) => r.data),
  });
}

export function useAdminServiceResources(serviceKey: string | null) {
  return useQuery({
    queryKey: ["admin", "system", "resources", serviceKey],
    queryFn: () => adminApi.getServiceResources(serviceKey as string).then((r) => r.data),
    enabled: serviceKey !== null,
  });
}

export function useAdminSettings() {
  return useQuery({
    queryKey: ["admin", "settings"],
    queryFn: () => adminApi.getSettings().then((r) => r.data),
  });
}

export function useAdminUpdateSettings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      adminApi.updateSettings(data).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "settings"] });
      toast.success("تنظیمات ذخیره شد");
    },
    onError: (err: unknown) => {
      toast.error(extractErrorDetail(err) ? translateDetail(extractErrorDetail(err)!) : "خطا در ذخیره تنظیمات");
    },
  });
}

export function useAdminTickets(search = "", planId?: number | null, sort = "newest") {
  return useQuery({
    queryKey: ["admin", "tickets", search, planId ?? null, sort],
    queryFn: () => {
      const params: Record<string, unknown> = {};
      if (search.trim()) params.search = search.trim();
      if (planId !== undefined && planId !== null) params.plan_id = planId;
      if (sort && sort !== "newest") params.sort = sort;
      return adminApi.getTickets(params).then((r) => r.data);
    },
  });
}

export function useAdminMe() {
  return useQuery({
    queryKey: ["admin", "me"],
    queryFn: () => adminApi.me().then((r) => r.data),
    staleTime: 60 * 1000,
  });
}

export function useAdminTicket(ticketId: number | null) {
  return useQuery({
    queryKey: ["admin", "tickets", ticketId],
    queryFn: () => adminApi.getTicket(ticketId as number).then((r) => r.data),
    enabled: ticketId !== null,
  });
}

export function useAdminReplyTicket(ticketId: number | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: { message: string }) =>
      adminApi.replyTicket(ticketId as number, data).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "tickets", ticketId] });
      queryClient.invalidateQueries({ queryKey: ["admin", "tickets"] });
      toast.success("پاسخ ارسال شد");
    },
    onError: (err: unknown) => {
      toast.error(extractErrorDetail(err) ? translateDetail(extractErrorDetail(err)!) : "خطا در ارسال پاسخ");
    },
  });
}

export function useAdminCloseTicket(ticketId: number | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => adminApi.closeTicket(ticketId as number).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "tickets", ticketId] });
      queryClient.invalidateQueries({ queryKey: ["admin", "tickets"] });
      toast.success("تیکت بسته شد");
    },
    onError: () => toast.error("خطا در بستن تیکت"),
  });
}

export function useAdminReopenTicket(ticketId: number | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => adminApi.reopenTicket(ticketId as number).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "tickets", ticketId] });
      queryClient.invalidateQueries({ queryKey: ["admin", "tickets"] });
      toast.success("تیکت باز شد");
    },
    onError: () => toast.error("خطا در باز کردن تیکت"),
  });
}

export function useAdminRoles() {
  return useQuery({
    queryKey: ["admin", "roles"],
    queryFn: () => adminApi.getRoles().then((r) => r.data),
  });
}

export function useAdminCreateRole() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      adminApi.createRole(data).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "roles"] });
      toast.success("نقش ساخته شد");
    },
    onError: (err: unknown) => {
      toast.error(extractErrorDetail(err) ? translateDetail(extractErrorDetail(err)!) : "خطا در ساخت نقش");
    },
  });
}

export function useAdminUpdateRole() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) =>
      adminApi.updateRole(id, data).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "roles"] });
      toast.success("نقش به‌روزرسانی شد");
    },
    onError: (err: unknown) => {
      toast.error(extractErrorDetail(err) ? translateDetail(extractErrorDetail(err)!) : "خطا در به‌روزرسانی نقش");
    },
  });
}

export function useAdminDeleteRole() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => adminApi.deleteRole(id).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "roles"] });
      toast.success("نقش حذف شد");
    },
    onError: (err: unknown) => {
      toast.error(extractErrorDetail(err) ? translateDetail(extractErrorDetail(err)!) : "خطا در حذف نقش");
    },
  });
}

export function useAdminExpenses() {
  return useQuery({
    queryKey: ["admin", "expenses"],
    queryFn: () => adminApi.getExpenses().then((r) => r.data),
  });
}

export function useAdminCreateExpense() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: { amount: number; description: string }) =>
      adminApi.createExpense(data).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "expenses"] });
      queryClient.invalidateQueries({ queryKey: ["admin", "stats"] });
      toast.success("هزینه ثبت شد");
    },
    onError: (err: unknown) => {
      toast.error(extractErrorDetail(err) ? translateDetail(extractErrorDetail(err)!) : "خطا در ثبت هزینه");
    },
  });
}

export function useAdminUpdateExpense() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ expenseId, data }: { expenseId: number; data: { amount: number; description: string } }) =>
      adminApi.updateExpense(expenseId, data).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "expenses"] });
      queryClient.invalidateQueries({ queryKey: ["admin", "stats"] });
      toast.success("هزینه ویرایش شد");
    },
    onError: (err: unknown) => {
      toast.error(extractErrorDetail(err) ? translateDetail(extractErrorDetail(err)!) : "خطا در ویرایش هزینه");
    },
  });
}

// ============================================================================
// Admin Subscription Plans
// ============================================================================

export function useAdminPlans() {
  return useQuery({
    queryKey: ["admin", "plans"],
    queryFn: () => adminApi.getPlans().then((r) => r.data),
  });
}

export function useAdminPlanModels() {
  return useQuery({
    queryKey: ["admin", "plans", "models"],
    queryFn: () => adminApi.getPlanModels().then((r) => r.data),
    staleTime: 60 * 1000,
  });
}

export function useAdminCodecraftModels(enabled: boolean) {
  return useQuery({
    queryKey: ["admin", "plans", "codecraft-models"],
    queryFn: () => adminApi.getCodecraftPlanModels().then((r) => r.data),
    staleTime: 60 * 1000,
    enabled,
  });
}

export function useAdminPlanUsers(search: string, ids: number[]) {
  return useQuery({
    queryKey: ["admin", "plans", "users", search, ids],
    queryFn: () => adminApi.getPlanUsers(search, ids).then((r) => r.data),
    staleTime: 30 * 1000,
  });
}

export function useAdminCreatePlan() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      adminApi.createPlan(data).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "plans"] });
      toast.success("پلن ساخته شد");
    },
    onError: (err: unknown) => {
      toast.error(extractErrorDetail(err) ? translateDetail(extractErrorDetail(err)!) : "خطا در ساخت پلن");
    },
  });
}

export function useAdminUpdatePlan() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) =>
      adminApi.updatePlan(id, data).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "plans"] });
      toast.success("پلن به‌روزرسانی شد");
    },
    onError: (err: unknown) => {
      toast.error(extractErrorDetail(err) ? translateDetail(extractErrorDetail(err)!) : "خطا در به‌روزرسانی پلن");
    },
  });
}

export function useAdminDeletePlan() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => adminApi.deletePlan(id).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "plans"] });
      toast.success("پلن حذف شد");
    },
    onError: (err: unknown) => {
      toast.error(extractErrorDetail(err) ? translateDetail(extractErrorDetail(err)!) : "خطا در حذف پلن");
    },
  });
}

// ============================================================================
// Admin System Messages (announcements)
// ============================================================================

export function useAdminMessages() {
  return useQuery({
    queryKey: ["admin", "messages"],
    queryFn: () => adminApi.getMessages().then((r) => r.data),
  });
}

export function useAdminCreateMessage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      adminApi.createMessage(data).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "messages"] });
      toast.success("اطلاع‌رسانی ساخته شد");
    },
    onError: (err: unknown) => {
      toast.error(extractErrorDetail(err) ? translateDetail(extractErrorDetail(err)!) : "خطا در ساخت اطلاع‌رسانی");
    },
  });
}

export function useAdminUpdateMessage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: Record<string, unknown> }) =>
      adminApi.updateMessage(id, data).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "messages"] });
      toast.success("اطلاع‌رسانی به‌روزرسانی شد");
    },
    onError: (err: unknown) => {
      toast.error(extractErrorDetail(err) ? translateDetail(extractErrorDetail(err)!) : "خطا در به‌روزرسانی اطلاع‌رسانی");
    },
  });
}

export function useAdminDeleteMessage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => adminApi.deleteMessage(id).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "messages"] });
      toast.success("اطلاع‌رسانی حذف شد");
    },
    onError: (err: unknown) => {
      toast.error(extractErrorDetail(err) ? translateDetail(extractErrorDetail(err)!) : "خطا در حذف اطلاع‌رسانی");
    },
  });
}

// ============================================================================
// Admin Partners (Partner Integration)
// ============================================================================

export function useAdminPartners(params?: Record<string, unknown>) {
  return useQuery({
    queryKey: ["admin", "partners", params ?? {}],
    queryFn: () => adminApi.getPartners(params).then((r) => r.data),
  });
}

export function useAdminFactors(params?: Record<string, unknown>) {
  return useQuery({
    queryKey: ["admin", "factors", params],
    queryFn: () => adminApi.getFactors(params).then((r) => r.data),
  });
}

export function useAdminPartnerDetail(id: number | null) {
  return useQuery({
    queryKey: ["admin", "partners", id],
    queryFn: () => adminApi.getPartner(id as number).then((r) => r.data),
    enabled: id != null,
  });
}

export function useAdminPartnerUsers(id: number | null) {
  return useQuery({
    queryKey: ["admin", "partners", id, "users"],
    queryFn: () => adminApi.getPartnerUsers(id as number).then((r) => r.data),
    enabled: id != null,
  });
}

export function useAdminCreatePartner() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: { name: string; code: string; initial_balance?: number; currency?: string; plan_id?: number | null; representative_user_id?: number | null }) =>
      adminApi.createPartner(data).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "partners"] });
      toast.success("شریک (Partner) ساخته شد");
    },
    onError: (err: unknown) => {
      toast.error(extractErrorDetail(err) ? translateDetail(extractErrorDetail(err)!) : "خطا در ساخت شریک");
    },
  });
}

export function useAdminUpdatePartner() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: Record<string, unknown> }) =>
      adminApi.updatePartner(id, data).then((r) => r.data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["admin", "partners"] });
      queryClient.invalidateQueries({ queryKey: ["admin", "partners", variables.id] });
      toast.success("شریک به‌روزرسانی شد");
    },
    onError: (err: unknown) => {
      toast.error(extractErrorDetail(err) ? translateDetail(extractErrorDetail(err)!) : "خطا در به‌روزرسانی شریک");
    },
  });
}

export function useAdminCreatePartnerKey() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => adminApi.createPartnerKey(id).then((r) => r.data),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ["admin", "partners", id] });
      queryClient.invalidateQueries({ queryKey: ["admin", "partners"] });
    },
    onError: (err: unknown) => {
      toast.error(extractErrorDetail(err) ? translateDetail(extractErrorDetail(err)!) : "خطا در ساخت کلید Partner");
    },
  });
}

export function useAdminDeletePartner() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => adminApi.deletePartner(id).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "partners"] });
      toast.success("شریک حذف شد");
    },
    onError: (err: unknown) => {
      toast.error(extractErrorDetail(err) ? translateDetail(extractErrorDetail(err)!) : "خطا در حذف شریک");
    },
  });
}

export function useAdminDisablePartnerKey() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ partnerId, keyId }: { partnerId: number; keyId: number }) =>
      adminApi.disablePartnerKey(partnerId, keyId).then((r) => r.data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["admin", "partners", variables.partnerId] });
      queryClient.invalidateQueries({ queryKey: ["admin", "partners"] });
      toast.success("کلید Partner غیرفعال شد");
    },
    onError: (err: unknown) => {
      toast.error(extractErrorDetail(err) ? translateDetail(extractErrorDetail(err)!) : "خطا در غیرفعال‌سازی کلید");
    },
  });
}

// ============================================================================
// System Messages (user-facing dashboard)
// ============================================================================

export function useAnnouncements(placement?: string) {
  return useQuery({
    queryKey: ["announcements", placement ?? "all"],
    queryFn: () => messagesApi.getActive(placement).then((r) => r.data),
    refetchInterval: 60 * 1000,
  });
}

// ============================================================================
// Profile
// ============================================================================

export function useProfile() {
  return useQuery({
    queryKey: ["profile"],
    queryFn: () => profileApi.get().then((r) => r.data),
  });
}

export function useUpdateProfile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      profileApi.update(data).then((r) => r.data),
    onSuccess: (data) => {
      queryClient.setQueryData(["me"], data);
      queryClient.invalidateQueries({ queryKey: ["profile"] });
      toast.success("اطلاعات حساب به‌روزرسانی شد");
    },
    onError: (err: unknown) => {
      toast.error(extractErrorDetail(err) ? translateDetail(extractErrorDetail(err)!) : "خطا در به‌روزرسانی اطلاعات");
    },
  });
}

export function useChangePassword() {
  return useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      profileApi.changePassword(data).then((r) => r.data),
    onSuccess: () => toast.success("رمز عبور با موفقیت تغییر کرد"),
    onError: (err: unknown) => {
      toast.error(extractErrorDetail(err) ? translateDetail(extractErrorDetail(err)!) : "خطا در تغییر رمز عبور");
    },
  });
}

export function useSendVerification() {
  return useMutation({
    mutationFn: ({ field, value }: { field: string; value?: string }) =>
      profileApi.sendVerify(field, value).then((r) => r.data),
    onError: (err: Record<string, unknown>) => {
      const detail2 = (err.response as Record<string, unknown>)?.data as Record<string, unknown>;
      const detail = detail2?.detail;
      const msg = typeof detail === "object" && detail !== null ? (detail as Record<string, unknown>).message as string : (detail as string);
      toast.error(msg || (detail as string) || "خطا در ارسال کد");
    },
  });
}

export function useConfirmVerification() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ field, code }: { field: string; code: string }) =>
      profileApi.confirmVerify(field, code).then((r) => r.data),
    onSuccess: (data) => {
      queryClient.setQueryData(["me"], data);
      queryClient.invalidateQueries({ queryKey: ["profile"] });
      toast.success("احراز هویت موفق بود");
    },
    onError: (err: unknown) => {
      toast.error(extractErrorDetail(err) ? translateDetail(extractErrorDetail(err)!) : "خطا در تأیید کد");
    },
  });
}

// ============================================================================
// Subscription
// ============================================================================

export function useSubscription() {
  return useQuery({
    queryKey: ["subscription"],
    queryFn: () => subscriptionApi.getMe().then((r) => r.data),
  });
}

export function useSubscriptionUsage(enabled = true) {
  return useQuery({
    queryKey: ["subscription", "usage"],
    queryFn: () => subscriptionApi.getUsage().then((r) => r.data),
    enabled,
  });
}

export function useSubscriptionUsageTimeseries(enabled = true, metric: "tokens" | "requests" = "tokens") {
  return useQuery({
    queryKey: ["subscription", "usage", "timeseries", metric],
    queryFn: () => subscriptionApi.getUsageTimeseries(metric).then((r) => r.data),
    enabled,
  });
}

export function useSubModels() {
  return useQuery({
    queryKey: ["sub", "models"],
    queryFn: () => subApi.getModels().then((r) => r.data),
    staleTime: 60 * 1000,
  });
}

export function useSubModelDetail(modelName: string | null) {
  return useQuery({
    queryKey: ["sub", "models", modelName],
    queryFn: () => subApi.getModelDetail(modelName as string).then((r) => r.data),
    enabled: modelName != null,
    staleTime: 60 * 1000,
  });
}

export function useSubscriptionHistory(enabled = true) {
  return useQuery({
    queryKey: ["subscription", "history"],
    queryFn: () => subscriptionApi.getHistory().then((r) => r.data),
    enabled,
  });
}

export function useUpgradeSubscription() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: { plan_id: number; duration: number }) =>
      subscriptionApi.upgrade(data).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["subscription"] });
      queryClient.invalidateQueries({ queryKey: ["wallet", "balance"] });
      queryClient.invalidateQueries({ queryKey: ["wallet", "transactions"] });
      toast.success("اشتراک شما فعال شد");
    },
    onError: (err: unknown) => {
      toast.error(extractErrorDetail(err) ? translateDetail(extractErrorDetail(err)!) : "خطا در ارتقای اشتراک");
    },
  });
}

// ============================================================================
// Config
// ============================================================================

export function useUsdRate() {
  return useQuery({
    queryKey: ["config", "usd-rate"],
    queryFn: () => configApi.getUsdRate().then((r) => r.data),
    staleTime: 5 * 60 * 1000,
    refetchInterval: 5 * 60 * 1000,
    refetchIntervalInBackground: true,
  });
}

// ============================================================================
// Models
// ============================================================================

export function useModels(params?: Record<string, unknown>) {
  return useQuery({
    queryKey: ["models", params],
    queryFn: () => modelsApi.list(params).then((r) => r.data),
    staleTime: 60 * 1000,
  });
}

export function useModelDetail(modelName: string | null) {
  return useQuery({
    queryKey: ["models", "detail", modelName],
    queryFn: () => modelsApi.detail(modelName as string).then((r) => r.data),
    enabled: Boolean(modelName),
    staleTime: 60 * 1000,
  });
}

// ============================================================================
// Support
// ============================================================================

export function useTickets() {
  return useQuery({
    queryKey: ["support", "tickets"],
    queryFn: () => supportApi.listTickets().then((r) => r.data),
  });
}

export function useTicketLimits() {
  return useQuery({
    queryKey: ["support", "limits"],
    queryFn: () => supportApi.getLimits().then((r) => r.data),
  });
}

export function useTicketNotifications() {
  return useQuery({
    queryKey: ["support", "notifications"],
    queryFn: () => supportApi.getNotifications().then((r) => r.data),
    refetchInterval: 15000,
  });
}

export function useMarkTicketRead(ticketId: number | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => supportApi.markTicketRead(ticketId as number).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["support", "notifications"] });
      queryClient.invalidateQueries({ queryKey: ["support", "tickets"] });
    },
    onError: (err: unknown) => {
      toast.error(extractErrorDetail(err) ? translateDetail(extractErrorDetail(err)!) : "خطا در ثبت خوانده‌شدن تیکت");
    },
  });
}

export function useTicket(ticketId: number | null) {
  return useQuery({
    queryKey: ["support", "tickets", ticketId],
    queryFn: () => supportApi.getTicket(ticketId as number).then((r) => r.data),
    enabled: ticketId !== null,
  });
}

export function useCreateTicket() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      supportApi.createTicket(data, (data.files as File[] | undefined) || []).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["support", "tickets"] });
      queryClient.invalidateQueries({ queryKey: ["support", "limits"] });
      toast.success("تیکت شما ثبت شد");
    },
    onError: (err: unknown) => {
      toast.error(extractErrorDetail(err) ? translateDetail(extractErrorDetail(err)!) : "خطا در ثبت تیکت");
    },
  });
}

export function useReplyTicket(ticketId: number | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: { message: string; files?: File[] }) =>
      supportApi.replyTicket(ticketId as number, data, data.files || []).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["support", "tickets", ticketId] });
      queryClient.invalidateQueries({ queryKey: ["support", "tickets"] });
      queryClient.invalidateQueries({ queryKey: ["support", "limits"] });
    },
    onError: (err: unknown) => {
      toast.error(extractErrorDetail(err) ? translateDetail(extractErrorDetail(err)!) : "خطا در ارسال پیام");
    },
  });
}

export function useCloseTicket(ticketId: number | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => supportApi.closeTicket(ticketId as number).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["support", "tickets", ticketId] });
      queryClient.invalidateQueries({ queryKey: ["support", "tickets"] });
      queryClient.invalidateQueries({ queryKey: ["support", "limits"] });
      toast.success("تیکت بسته شد");
    },
    onError: () => toast.error("خطا در بستن تیکت"),
  });
}

// ============================================================================
// Partner Representative
// ============================================================================

export function usePartnerRepStatus() {
  return useQuery({
    queryKey: ["partner-rep", "status"],
    queryFn: () => partnerRepApi.status().then((r) => r.data),
    retry: false,
    staleTime: 5 * 60 * 1000,
  });
}

export function usePartnerRepDashboard() {
  return useQuery({
    queryKey: ["partner-rep", "dashboard"],
    queryFn: () => partnerRepApi.dashboard().then((r) => r.data),
    retry: false,
  });
}

export function usePartnerRepUsers(
  params?: { search?: string; sort_by?: string; sort_dir?: string }
) {
  return useQuery({
    queryKey: ["partner-rep", "users", params],
    queryFn: () => partnerRepApi.listUsers(params).then((r) => r.data),
    retry: false,
  });
}

export function usePartnerRepUserKeys(userId: number | null) {
  return useQuery({
    queryKey: ["partner-rep", "users", userId, "keys"],
    queryFn: () => partnerRepApi.listUserKeys(userId as number).then((r) => r.data),
    enabled: userId !== null,
    retry: false,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
  });
}

export function usePartnerRepUserSummary(
  userId: number | null,
  params?: Record<string, unknown>
) {
  return useQuery({
    queryKey: ["partner-rep", "users", userId, "summary", params],
    queryFn: () => partnerRepApi.userSummary(userId as number, params).then((r) => r.data),
    enabled: userId !== null,
    retry: false,
    staleTime: 30 * 1000,
  });
}

export function usePartnerRepUserTimeseries(
  userId: number | null,
  duration: string
) {
  return useQuery({
    queryKey: ["partner-rep", "users", userId, "timeseries", duration],
    queryFn: () =>
      partnerRepApi.userTimeseries(userId as number, { duration }).then((r) => r.data),
    enabled: userId !== null,
    retry: false,
    staleTime: 30 * 1000,
  });
}

export function usePartnerRepUserDashboard(
  userId: number | null,
  duration: string,
  metric: string
) {
  return useQuery({
    queryKey: ["partner-rep", "users", userId, "dashboard", duration, metric],
    queryFn: () =>
      partnerRepApi
        .userDashboard(userId as number, { duration, metric })
        .then((r) => r.data),
    enabled: userId !== null,
    retry: false,
    staleTime: 30 * 1000,
  });
}
